import { useRef } from "react";
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Image as ImageIcon, LayersOutlined } from "@mui/icons-material";

import type { FlexibleDataItem, SpatialDatasetProcessingStatus } from "../../../types";
import { getSafeThumbnailUrl } from "../../../utilities/mediaUtils";
import { getDataItemDescription, getDataItemName } from "../../../utilities/dataSourceAdapters";
import ResultNotFound from "../ResultNotFound";
import StatusText from "./components/StatusText";
import { DataGridViewRendererProps } from "./DataGrid";
import ListViewSkeleton from "./components/ListViewSkeleton";
import { SafeThumbnailImage } from "./components/SafeThumbnailImage";
import useIntersectionObserver from "../../../hooks/useIntersectionObserver";
import { SPATIAL_STATUS_TILE_LABEL } from "../../../constants/spatialDataset";
import SpatialDatasetVisibilityCheckbox from "../../spatial-datasets/SpatialDatasetVisibilityCheckbox";

interface DataListViewProps extends DataGridViewRendererProps {
  columns?: string[] | { name: string; accessorKey: string }[];
}

function valueIsDate(value: string) {
  return !isNaN(Date.parse(value));
}

/**
 * Format given date into dd/mm/YYYY
 * @returns {string} formatted value
 * */
function formatDate(value: string | Date): string {
  let date = value;
  if (typeof date === "string") {
    date = new Date(value);
  }
  return date.toLocaleString("en-AU", { dateStyle: "medium" });
}

export function TableSkeleton({ rows = 10, columns }: { rows?: number; columns?: number }) {
  const renderRows = Array.from({ length: rows }).map((_, idx) => {
    return <ListViewSkeleton key={`row-skeleton-${idx}`} column={columns} />;
  });
  return renderRows;
}

export default function DataListView({
  data = [],
  currentEndpoint,
  onSelect,
  isPending,
  columns = [],
  isAllDataFinishLoading = false,
  sx = {},
  activeEntityId,
  onLoadMore,
  hasMoreToLoad,
  isMoreLoading,
  viewportVisibleIds,
  ...restProps
}: DataListViewProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const dataRowCount = data.length;

  const { intersectElementRef } = useIntersectionObserver((isIntersect) => {
    if (isIntersect) {
      onLoadMore && onLoadMore();
    }
  });

  // Show extra two row when more data is being fetch
  const loaderRowCount = isAllDataFinishLoading ? 1 : isMoreLoading ? 2 : 0;

  // On pending state show 5 rows as loading skeleton
  const totalRows = isPending ? 16 : dataRowCount + loaderRowCount;

  const rowVirtualizer = useVirtualizer({
    count: totalRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 84,
    overscan: 5
  });

  return (
    <Box
      ref={parentRef}
      {...restProps}
      sx={{
        position: "relative",
        px: "1rem",
        width: "100%",
        height: "calc(100vh - 15rem)",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        ...sx
      }}
      className='scroller'
    >
      <TableContainer
        sx={{
          tableLayout: "auto",
          position: "relative",
          height: `${rowVirtualizer.getTotalSize()}px`,
          overflow: "unset"
        }}
      >
        <Table stickyHeader sx={{ height: "auto" }}>
          <TableHead sx={{ "& *": { fontWeight: 400 } }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  maxWidth: "12rem",
                  minWidth: "12rem",
                  backgroundColor: "#1D2939",
                  color: "#fff",
                  borderTop: "1px solid",
                  borderColor: "#344054",
                  py: "0.28rem",
                  pl: 0
                }}
              >
                <Typography variant='body2'>List of places</Typography>
              </TableCell>
              {/* Add Source column for related items */}
              {columns.map((column) => {
                const colName =
                  typeof column === "object" ? column.name : column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, " ");
                return (
                  <TableCell
                    key={colName}
                    sx={{
                      minWidth: "4.567rem",
                      backgroundColor: "#1D2939",
                      borderTop: "1px solid",
                      borderColor: "#344054",
                      color: "#98A2B3",
                      py: "0.28rem"
                    }}
                  >
                    <Typography variant='body2' textAlign='left' noWrap>
                      {colName}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Initial pending state */}
            {isPending && <TableSkeleton rows={16} />}

            {!isPending &&
              rowVirtualizer.getVirtualItems().map((virtualRow, idx) => {
                const item = data?.[virtualRow.index];
                const imageUrl = item ? getSafeThumbnailUrl(item, currentEndpoint) || getSafeThumbnailUrl(item) : undefined;
                const itemName = item ? getDataItemName(item, currentEndpoint || undefined) : "";
                const isLoaderRow = virtualRow.index >= dataRowCount;
                const isDimmed = !isLoaderRow && !!viewportVisibleIds && item && !viewportVisibleIds.has(item.id);

                // Spatial dataset status (§6c list view)
                const isSpatial = currentEndpoint?.entityType === "spatial-dataset";
                const isTabular = isSpatial && item && (item as any).dataset_type === "tabular";
                const spatialStatus =
                  isSpatial && item ? (((item as any).processing_status as SpatialDatasetProcessingStatus) ?? "pending") : null;
                // Tabular datasets never produce a map display_url, so a completed
                // tabular dataset is "done" even without one.
                const spatialNotViewable =
                  isSpatial && item && (spatialStatus !== "completed" || (!isTabular && !(item as any).display_url));

                return (
                  <TableRow
                    hover
                    key={item?.id}
                    onClick={() => !isLoaderRow && onSelect(item)}
                    sx={{
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      maxHeight: `${virtualRow.size}px!important`,
                      backgroundColor: item && activeEntityId === item?.id ? "primary.main" : "transparent",
                      color: "#fff",
                      opacity: isDimmed ? 0.4 : 1,
                      transform: `translateY(${virtualRow.start - idx * virtualRow.size}px)`,
                      cursor: isLoaderRow ? "default" : "pointer"
                    }}
                  >
                    {/* Load-more skeleton rows */}
                    {!isPending && isLoaderRow && isMoreLoading && <ListViewSkeleton column={columns.length} cellOnly />}
                    {/* End of data indicator */}
                    {isAllDataFinishLoading && !isMoreLoading && isLoaderRow && data?.length > 0 && (
                      <TableCell sx={{ border: 0 }} colSpan={(columns?.length || 1) + 1}>
                        <StatusText
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                          }}
                        />
                      </TableCell>
                    )}

                    {!isLoaderRow && item && (
                      <>
                        <TableCell
                          sx={{
                            width: "80px",
                            minWidth: "80px",
                            maxWidth: "12rem",
                            textAlign: "center",
                            borderColor: "#1f3046",
                            p: 0,
                            maxHeight: "84px"
                          }}
                        >
                          <Box sx={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                            {isSpatial && spatialStatus === "completed" && !isTabular && (
                              <Box onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                                <SpatialDatasetVisibilityCheckbox item={item} />
                              </Box>
                            )}
                            {imageUrl ? (
                              <SafeThumbnailImage
                                item={item}
                                src={imageUrl}
                                alt=''
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "0.5rem",
                                  objectFit: "cover",
                                  boxShadow: 1
                                }}
                              />
                            ) : // Spatial datasets that have no thumbnail yet use a layers icon
                            isSpatial ? (
                              <LayersOutlined sx={{ width: 32, height: 32, color: "#707171" }} />
                            ) : (
                              <ImageIcon sx={{ width: 32, height: 32, color: "#707171" }} />
                            )}

                            <Box sx={{ display: "flex", flexDirection: "column", gap: "0.2rem", minWidth: 0 }}>
                              <Tooltip title={itemName}>
                                <Typography
                                  color='#fff'
                                  variant='h6'
                                  textTransform='capitalize'
                                  whiteSpace='wrap'
                                  textAlign='left'
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: "2",
                                    WebkitBoxOrient: "vertical"
                                  }}
                                >
                                  {itemName}
                                </Typography>
                              </Tooltip>
                              {/* Spatial processing status chip in list row */}
                              {isSpatial && spatialNotViewable && spatialStatus && (
                                <Chip
                                  label={SPATIAL_STATUS_TILE_LABEL[spatialStatus]}
                                  size='small'
                                  color={spatialStatus === "failed" ? "error" : "default"}
                                  sx={{ alignSelf: "flex-start", fontSize: "0.65rem", height: 18 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {columns?.map((column) => {
                          const colKey = typeof column === "string" ? column : column.accessorKey;
                          const cellData =
                            column === "description"
                              ? getDataItemDescription(item, currentEndpoint || undefined) || "-"
                              : typeof (item as any)[colKey] === "object"
                                ? JSON.stringify((item as any)[colKey])
                                : valueIsDate(item[colKey as keyof FlexibleDataItem] as string)
                                  ? formatDate(item[colKey as keyof FlexibleDataItem] as string)
                                  : String((item as any)[colKey] || "") || "-";
                          return (
                            <TableCell
                              key={colKey}
                              sx={{
                                verticalAlign: "middle",
                                color: "#98A2B3",
                                borderColor: "#1f3046",
                                maxHeight: "84px"
                              }}
                            >
                              <Tooltip title={cellData}>
                                <Typography
                                  variant='body2'
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    display: "-webkit-box",
                                    WebkitLineClamp: "2",
                                    WebkitBoxOrient: "vertical"
                                  }}
                                >
                                  {cellData}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </>
                    )}
                  </TableRow>
                );
              })}

            {data?.length > 0 && hasMoreToLoad && (
              <TableRow>
                <TableCell sx={{ border: 0 }} colSpan={columns?.length + 1}>
                  <Box
                    component='div'
                    ref={intersectElementRef}
                    sx={{
                      height: "1px",
                      width: "100%",
                      position: "absolute",
                      top: rowVirtualizer.getTotalSize() + 24,
                      left: 0
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            {/* Show Result not found message*/}
            {!isPending && (!data || data.length === 0) && (
              <TableRow>
                <TableCell sx={{ border: 0 }} colSpan={(columns?.length || 1) + 1}>
                  <ResultNotFound sx={{ minHeight: "calc(100dvh - 24rem)" }} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
